
import ToastManager from '~/fc/ToastManager'
import Job from '~/fc/Converter/job'
import { STATUS_READY, STATUS_DONE, STATUS_CONVERTING, STATUS_LABELS, STATUS_LIMIT_REACH_ERROR, STATUS_ERROR } from '~/fc/Constants/job-status'
import { progressBarClass } from '~/fc/Helpers/UIHelpers'
import { uuidv4 } from '~/fc/Helpers/FileHelpers'
import { truncate } from '~/fc/Helpers/StringHelpers'
import { parseSelection } from '~/fc/advancedOptionHelper'
import { loadScript } from '~/fc/Helpers/DOMHelper'
import FileInput from /* webpackChunkName: "FileInput" */ '~/components/file-io/FileInput'
import TargetDeterminer from '~/fc/Target/TargetDeterminer'
import TargetSelector from '~/fc/Target/TargetSelector'
import { initiateSocketConnection } from '~/fc/socket'
import { LoaderIcon } from 'vue-feather-icons'
import jobWatcher from '../../mixins/jobWatcher'
import { HHMMSSToSeconds, secondsToHHMMSS } from '~/fc/Helpers/dateFormatter'

export default {
    name: 'PreviewTemplate',
    mixins: [jobWatcher],
    components: {
        FileInput,
        LoaderIcon,
        AdvancedOptions: () => import(/* webpackChunkName: "AdvancedOptions" */ '../../components/file-io/AdvancedOptions'),
        TargetSelectorTrigger: () => import(/* webpackChunkName: "TargetSelectorTrigger" */ '../../components/file-io/TargetSelectorTrigger'),
        PreviewLayout: () => import(/* webpackChunkName: "PreviewLayout" */ './PreviewLayout'),
        Banner: () => import( /* webpackChunkName: "Banner" */ '~/components/ui/Banner'),
        BannerContainer: () => import( /* webpackChunkName: "Banner" */ '~/components/ui/BannerContainer')
    },
    data: () => ({
        cropComponent: false,
        cropDimensions: false,
        forceDimention: false,
        videoPreview: null,
        errorMsg: false,
        targetList: [],
        target: '',
        name: '',
        fileSelected: false,
        hasError: false,
        customVariables: {},
        videoKey: 1,
        previewReady: false,
        enableCropper: false,
        videoOptions: {
            autoplay: false,
            controls: true,
            sources: []
        },
        statusText: STATUS_LABELS,
        limitReached: false
    }),
    computed: {
        info() { return this.$store.state.info },
        list() { return this.$store.state.list },
        file() { return this.$store.state.list[0] },
        jobs() { return this.$store.state.items.jobs },
        isMobile() { return this.$store.state.store.isMobile },
        toastManager() { return (new ToastManager(this)) },
        selectedTarget() { return this.$store.state.advancedSetting.targetForFile[this.file.id] || {} },
        hasFiles() { return !!this.$store.state.list.length },
        percent() { return this.job && this.job.progress ? this.job.progress : 0 },
        jobID() { return (this.file && this.file.job_id) ? this.file.job_id : '' },
        itemStatus() { return (this.job) ? (this.job.status || STATUS_READY) : STATUS_DONE },
        rejected() { return this.itemStatus === STATUS_LIMIT_REACH_ERROR },
        progressBarClass() { return progressBarClass(this.itemStatus) },
        showLoading() { return this.itemStatus === STATUS_CONVERTING && this.percent < 1 },
        showConvertBtn() { return this.itemStatus === STATUS_DONE && !this.targetList.length },
        job() { return (this.jobID && this.jobs[this.jobID]) ? this.jobs[this.jobID] : false },
        enabled() { return !this.info.toolPage ? true : (this.info.customVariables && this.info.customVariables.targets) },
        upgradingMidConversion () { return this.$store.state.banner.upgradingMidConversion }
    },
    watch: {
        itemStatus(newVal, oldVal) {
            if (newVal !== oldVal && newVal === STATUS_DONE) {
                const file = { ...this.list[0] }

                file.serverURL = this.job.output
                this.$store.commit('setList', [file])

                this.videoOptions.sources = [
                    {
                        src: this.job.output,
                        type: 'video/mp4'
                    }
                ]
                this.showPreview()

                setTimeout(() => {
                    this.hasError = false
                }, 300)
            } else {
                if (newVal >= STATUS_ERROR && newVal != STATUS_LIMIT_REACH_ERROR) {
                    this.goToDownloadPageWithError(this.job, newVal)
                }
            }
        },
        list: {
            handler() {
                if (this.list && this.list.length) {
                    this.onFileChange()
                }
            }
        },
        hasFiles(val) {
            if (val) {
                function findPos(obj) {
                    let curtop = 0
                    if (obj.offsetParent) {
                        do {
                            curtop += obj.offsetTop - 80
                        } while (obj = obj.offsetParent)
                        return [curtop]
                    }
                }
                window.scroll(0, findPos(document.getElementById('preview-template')))
            }
        },
        async file(val) {
            if (val) {
                this.$store.commit('advancedSetting/copySelection', {
                    from: 'root',
                    to: val.id
                })
                await this.$store.dispatch('advancedSetting/setTarget', val)
                const target = await new TargetDeterminer({ ...val }).determine()
                if (target && this.info.operation === 'crop') {
                    this.$store.commit('advancedSetting/setTargetForFile', {
                        ready: true,
                        uid: this.file.id,
                        ext: target.ext,
                        type: this.info.type
                    })
                }
            }
        }
    },
    created() {
        if (this.info.customVariables) {
            this.customVariables = this.info.customVariables
        }
        this.$store.commit('updateBreadcrumbs', [
            {
                url: this.$route.path,
                label: this.info.header_h1
            }
        ])
    },
    mounted() {
        this.loadVideoJS()
        this.videoPreview = () => import('../../components/preview/VideoPreview.vue')

        this.initiateTarget()

        if (this.info.customVariables) {
            if (this.info.customVariables.enableCropper) {
                this.videoOptions.autoplay = true
                this.cropComponent = () => import('../../components/preview/VideoCropper.vue')
            }
            if (this.info.customVariables.forceTarget) {
                this.$store.commit('forceTarget', this.info.target)
            }

            if (this.info.customVariables.targets) {
                this.targetList = [
                    {
                        // eslint-disable-next-line no-useless-escape
                        label: this.info.type.replace(/(crop\-|\-trimmer)/g, ''),
                        links: this.info.customVariables.targets
                            .split(',')
                            .map(target => ({
                                label: target,
                                value: target.replace(' ', '-').toLowerCase()
                            }))
                    }
                ]
            }
        }
    },
    methods: {
        initiateTarget() {
            this.target = this.info && this.info.target ? this.info.target : ''
            this.$store.dispatch('advancedSetting/setDefaultTarget', this.target)
        },
        getSeconds(str) {
            const arr = str.split(':').map(a => parseFloat(a))
            return arr[0] * 60 * 60 + arr[1] * 60 + arr[2]
        },
        truncate(str, lim) {
            return truncate(str, lim)
        },
        async goToDownloadPage(onlyCopySelection = false) {
            const { advancedSetting } = this.$store.state
            const list =
                advancedSetting.perConversionType[advancedSetting.userData.conversionType.root] &&
                    advancedSetting.perConversionType[advancedSetting.userData.conversionType.root].list
                    ? advancedSetting.perConversionType[advancedSetting.userData.conversionType.root].list
                    : []
            const selection = parseSelection(
                list,
                advancedSetting.userData.allSelection.root
            )
            let start = HHMMSSToSeconds(selection[this.getTrimOptionNames('cut_start')])
            let end = HHMMSSToSeconds(selection[this.getTrimOptionNames('cut_end')])
            
            if(!(start === 0 && end === 0)) {
                if (start && (typeof start === 'string' || start instanceof String) && start.includes(':')) {
                    start = this.getSeconds(start)
                } else if (start) {
                    start = parseFloat(start)
                }
                if (end && (typeof end === 'string' || end instanceof String) && end.includes(':')) {
                    end = this.getSeconds(end)
                } else if (end) {
                    end = parseFloat(end)
                }
                const duration = this.$refs.preview && this.$refs.preview.player
                    ? this.$refs.preview.player.duration()
                    : null

                this.errorMsg = false
                if (!duration) { this.errorMsg = this.$t('please_wait_for_video_load') }
                if (start > end) {
                    this.errorMsg = this.$t('start_time_less_than_end_time')
                } else if (start < 0 || end < 0 || start > duration || end > duration) {
                    this.errorMsg = this.$t('time_cant_be_greater_than_duration')
                    // eslint-disable-next-line eqeqeq
                } else if ((start !== undefined) && (start == end)) {
                    this.errorMsg = this.$t('start_end_time_not_same')
                } else if (
                    this.customVariables.maxDuration &&
                    end - start > this.customVariables.maxDuration
                ) {
                    this.errorMsg = this.$t('duration_must_less_than_max').replace('COMMON_NAME', this.customVariables.commonName).replace('MAX_DURATION', this.customVariables.maxDuration)
                }
            }

            
            if (this.errorMsg) {
                this.toastManager.add('danger', this.errorMsg, this.errorMsg)
                return
            }

            this.$store.commit('advancedSetting/copySelection', {
                from: 'root',
                to: this.file.id
            })

            if (onlyCopySelection) return

            await this.$store.commit('setDownloadPage', {
                from: this.info.type,
                target: this.target,
                targetName: this.target,
                convertMoreText: this.info.convert_more_text,
                resultPageH1: this.info.result_page_h1
            })
            this.$router.push(`${this.$route.path.replace('/download', '').replace(/\/$/, '')}/download`)
        },
        async goToDownloadPageWithError (job, errorStatus) {
            this.$store.commit("items/setJob", {
                ...job,
                status: errorStatus,
                type: 'manipulation',
            });

            this.$store.commit('advancedSetting/copySelection', {
                from: 'root',
                to: this.file.id
            })

            this.$store.commit('setDownloadPage', {
                from: this.info.type,
                target: this.target,
                targetName: this.target,
                convertMoreText: this.info.convert_more_text,
                resultPageH1: this.info.result_page_h1
            })

            this.$store.commit('items/setStartConversionOnDownloadPage', false)
            this.$router.push(`${this.$route.path.replace('/download', '').replace(/\/$/, '')}/download`)
        },
        onDownloadStart() {
            // Only for crop page for now
            const file = this.updateFileId()
            let ext
            if (this.$store.state.advancedSetting.defaultTarget === 'keep-same') {
                this.$store.commit('advancedSetting/setTargetForFile', {
                    ready: true,
                    uid: file.id,
                    ext: file.ext,
                    type: this.info.type
                })
                ext = file.ext.toLowerCase()
            } else {
                this.$store.commit('advancedSetting/setTargetForFile', {
                    ready: true,
                    uid: file.id,
                    ext: this.$store.state.advancedSetting.defaultTarget,
                    type: this.info.type
                })
                ext = this.$store.state.advancedSetting.defaultTarget
            }
            if (
                this.info.customVariables.targets.split(',').includes(ext) ||
                ext === 'divx'
            ) {
                const as = Object.assign(
                    {},
                    JSON.parse(JSON.stringify(this.$store.state.advancedSetting.userData))
                )
                if (!as.allSelection.finalSelection) {
                    as.allSelection.finalSelection = {}
                }
                as.allSelection.finalSelection.video_codec = 'libx264'
                as.allSelection.finalSelection.video_crf = '16'

                this.$store.commit('advancedSetting/setUserData', as)
            }
        },
        updateFileId() {
            const file = { ...this.list[0] }

            file.id = file.id + '1'
            this.$store.commit('setList', [file])
            return file
        },
        showPreview() {
            this.previewReady = true
            this.videoKey++
        },
        onPreviewLoad() {
            if (this.customVariables.enableCropper) this.showCropper()
            this.setInitialTrim()
        },
        showCropper() {
            this.enableCropper = true
        },
        async onPreviewError() {
            this.$store.commit('advancedSetting/setAdvancedOptionsLoading', true)
            initiateSocketConnection()
            this.hasError = true
            this.previewReady = false

            if (!this.list[0]) return
            const file = { ...this.list[0] }

            const options = {
                avoid_upscaling: true,
                enable_faststart: true,
                video_compress_speed: 'superfast',
                video_crf: 23,
                pix_fmt: 'yuv420p',
                video_codec: 'h264',
                video_profile: 'baseline',
                video_level: 3,
                audio_codec: 'aac'
            }
            if (this.customVariables.options) {
                this.customVariables.options.forEach((option) => {
                    options[option.key] = option.value
                })
            }

            let importTask = {}

            if (file.file === 'url') {
                if (file.gtoken) {
                    importTask = {
                        operation: 'import/google-drive',
                        fileId: file.url,
                        gtoken: file.gtoken,
                        filename: file.name,
                        filesize: file.total
                    }
                } else {
                    importTask = {
                        operation: 'import/url',
                        url: file.url
                    }
                    if (file.name) {
                        importTask.filename = file.name
                    }
                }
            } else {
                importTask = {
                    operation: 'import/upload'
                }
            }

            const tasks = {
                import: importTask,
                convert: {
                    operation: 'convert',
                    input: 'import',
                    input_format: file.ext,
                    output_format: 'mp4',
                    options
                },
                export: {
                    operation: 'export/url',
                    input: 'convert'
                }
            }

            const job = new Job(tasks)

            await job.processSingleFile({
                file,
                returnJob: false,
                jobId: uuidv4()
            })

            /**
               * If the item does not goes to converting state by 30 seconds, we trigger a GA event
               */
            setTimeout(() => {
                if (this.itemStatus < STATUS_CONVERTING) {
                    this.$gtm.push({ event: 'upload_delay_300s' });
                }
            }, 300000)
        },
        onFileChange() {
            const videoFormats = ["mp4","webm","mov","mkv","3gp","f4v","f4p","m4v","dvr-ms","qt"];
            this.enableCropper = false
            const URL = window.URL || window.webkitURL
            if (!this.hasError && this.list.length) {
                const file = this.list[this.list.length - 1]
                const isURL = typeof file.file === "string" && file.file === "url";
                const videoSource = {
                    src: isURL ? file.src : URL.createObjectURL(file.file),
                    type: videoFormats.includes(file.ext)
                        ? `video/mp4`
                        : `video/${file.ext}`,
                };
                this.name = file.name
                this.videoOptions.sources = [videoSource];
                this.fileSelected = true
                this.forceDimention = false
                this.showPreview()
            }
        },
        getTrimOptionNames(name) {
            const optionNames = Object.values(this.$store.state.advancedSetting.userData.fieldNames)
            return optionNames.find(x => x.includes(name)) || name
        },
        /**
        * Sets the current video play time as trim start.
         */
        selectStart() {
            this.$store.commit('advancedSetting/setSelection', { uid: 'root', name: this.getTrimOptionNames('cut_start'), value: secondsToHHMMSS(Math.floor(window.player.currentTime())) })
        },
        /**
         * Sets the current video play time as trim end.
         */
        selectEnd() {
            this.$store.commit('advancedSetting/setSelection', { uid: 'root', name: this.getTrimOptionNames('cut_end'), value: secondsToHHMMSS(Math.floor(window.player.currentTime())) })
        },
        /**
         * Sets initial trim values. 0 as trim start and video duration as trim end.
         */
        setInitialTrim() {
            // this.$store.commit('advancedSetting/setSelection', { uid: 'root', name: this.getTrimOptionNames('cut_start'), value: '00:00:00' })
            // this.$store.commit('advancedSetting/setSelection', {uid: 'root', name: this.getTrimOptionNames('cut_end'), value: '00:00:00' });
        },
        onTargetChange(target) {
            this.target = target.ext
            this.$store.dispatch('advancedSetting/setDefaultTarget', target.ext)
            new TargetSelector(this).setFile(this.file).setTargetForFile(target, false)
        },
        loadVideoJS() {
            if (!window.videojs) { loadScript('https://vjs.zencdn.net/7.11.4/video.min.js', 'videojs', () => { }) }
        }
    }
}
